import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { enqueueSnackbar } from "notistack";
import routes from "../routing/routes";
import { store } from "../redux";
import { loggingOut } from "../redux/auth/actions";
import { history } from "../routing/history";
import { API_URL, CURRENT_API_VERSION } from "./apiConfig";
import { ApiHelper } from "./apiHelper";
import { getDynamicTranslation } from "../components/core/localization/localizationUtils";

const BASE_CONFIG: AxiosRequestConfig = {
  baseURL: API_URL + CURRENT_API_VERSION,
  xsrfHeaderName: "X-CSRFToken",
  xsrfCookieName: "csrftoken",
  withCredentials: true,
};

/**
 * Class that holds base CRUD functionality for API calls.
 */
let attempts = 0;
const maxAttempsWithError = 3;
let attemptUrl = "";

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  suppressSnackbars?: boolean;
}

const isErrorPayloadObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

export class BaseCrudService {
  protected axiosInstance: AxiosInstance;

  public constructor(config?: CustomAxiosRequestConfig, useInterceptor = true) {
    this.axiosInstance = axios.create({ ...BASE_CONFIG, ...config });

    this.axiosInstance.interceptors.request.use(
      (request) => request, // don't check for session if the flag is false
      this.handleErrorResponse
    );

    if (useInterceptor) {
      this.axiosInstance.interceptors.response.use(
        (response) => response,
        this.handleErrorResponseWrapper()
      );
    }
  }

  protected handleErrorResponseWrapper() {
    attempts++;
    return this.handleErrorResponse;
  }
  /**
   * Function that is responsible for intercepting all responses that come from API to check for errors.
   * In this specific case listens for 401 (unauthorized) and 403 (forbidden) errors (indicates that the user isn't logged in anymore)
   * and logs out automatically and also shows an info alert.
   * @param error Error object that holds information of the status, message and data of the error that occured.
   */
  protected handleErrorResponse(error: AxiosError) {
    const currentState = store.getState();
    const auth = currentState.auth;
    const tryLinkAccountWithoutLogin =
      currentState.linkAccount.tryLinkAccountWithoutLogin;

    const wasLoggedIn = auth.loggedIn; // check if state was logged in

    const errorStatus = error.response?.status;
    const errorObject = error.response?.data;
    const suppressSnackbars = (error.config as CustomAxiosRequestConfig)
      ?.suppressSnackbars;
    if (errorStatus === 400 || errorStatus === 409 || errorStatus === 422) {
      if (!suppressSnackbars) {
        // for 400 status codes show the error message coming from API
        if (isErrorPayloadObject(errorObject)) {
          for (const errorMessage of Object.values(errorObject)) {
            enqueueSnackbar(`${getDynamicTranslation(errorMessage, "snackbars")}`, {
              variant: "error",
            });
          }
        } else if (typeof errorObject === "string" && errorObject !== "") {
          enqueueSnackbar(getDynamicTranslation(errorObject, "snackbars"), {
            variant: "error",
          });
        } else {
          enqueueSnackbar(getDynamicTranslation("baseCrudService.default", "snackbars"), {
            variant: "error",
          });
        }
      }
    } else if (errorStatus === 404) {
      if (!suppressSnackbars) {
        enqueueSnackbar(getDynamicTranslation("baseCrudService.404", "snackbars"), {
          variant: "error",
        });
      }
    } else if (errorStatus === 403 || errorStatus === 401) {
      const errorMessages =
        errorObject && typeof errorObject === "object" ? Object.values(errorObject) : [];

      if (errorMessages.length > 0) {
        for (const errorMessage of errorMessages) {
          enqueueSnackbar(`${getDynamicTranslation(errorMessage, "snackbars")}`, {
            variant: "error",
          });
        }
      } else if (wasLoggedIn) {
        store.dispatch(loggingOut()); // set logout state
        enqueueSnackbar(
          getDynamicTranslation("baseCrudService.sessionExpired", "snackbars"),
          { variant: "info" }
        );
      } else {
        // ! this is a special case: show info alert if user tries to link accounts but is not logged in
        if (tryLinkAccountWithoutLogin) {
          enqueueSnackbar(
            getDynamicTranslation("baseCrudService.loginValidation", "snackbars"),
            { variant: "info" }
          );
        } else {
          enqueueSnackbar(getDynamicTranslation("baseCrudService.403", "snackbars"), {
            variant: "error",
          });
        }
      }
    } else if (errorStatus === 503) {
      // maintenance
      history.push(routes.maintenance);
    } else {
      enqueueSnackbar(getDynamicTranslation("baseCrudService.default", "snackbars"), {
        variant: "error",
      });
    }

    return Promise.reject<AxiosError>(error);
  }

  /**
   * Implements the /POST call to API endpoint (/url) via axios which creates one object of the given type.
   * @param url API path to which the call is sent
   * @param data data which is send to the API that should be created
   * @param params query parameters that are append to the url (are passed as JSON object)
   * @param config optional config for the API call e.g. setting specific headers
   */
  protected async create<T, R = AxiosResponse<T>>(
    url: string,
    data?: T,
    params?: string | object,
    config?: CustomAxiosRequestConfig | undefined
  ): Promise<R> {
    const urlWithParams = ApiHelper.parseUrlWithParams(url, params);
    // return axios.post(urlWithParams, data, config);
    return this.axiosInstance.post(urlWithParams, data, config);
  }

  /**
   * Implements the /GET call to API endpoint (/url/{id}) via axios to receive one object with the given id of the given type.
   * @param url API path to which the call is sent
   * @param id identifier of the object that should be received
   * @param config optional config for the API call e.g. setting specific headers
   */
  protected async get<T, R = AxiosResponse<T>>(
    url: string,
    id: string,
    config?: CustomAxiosRequestConfig | undefined
  ): Promise<R> {
    const urlWithParams = `${url}/${id}`;
    if (this.canAttempt(url)) {
      return this.axiosInstance.get(urlWithParams, config);
    } else return Promise.reject<R>({ response: undefined } as AxiosError);
  }

  /**
   * Implements the /GET call to API endpoint (/url) via axios to receive all objects of the given type.
   * @param url  API path to which the call is sent
   * @param params query parameters that are append to the url (are passed as JSON object)
   * @param config optional config for the API call e.g. setting specific headers
   */
  protected async getAll<T, R = AxiosResponse<T>>(
    url: string,
    params?: string | object,
    config?: CustomAxiosRequestConfig | undefined
  ): Promise<R> {
    const urlWithParams = ApiHelper.parseUrlWithParams(url, params);
    if (this.canAttempt(urlWithParams)) {
      return this.axiosInstance.get(urlWithParams, config);
    } else return Promise.reject<R>({ response: undefined } as AxiosError);
  }

  /**
   * Implements the /PATCH call to API endpoint (/url/{id}) via axios to edit (only parts of) one object with the given id of the given type.
   * @param url API path to which the call is sent
   * @param id identifier of the object that should be edited
   * @param data data which is send to the API that should be edited
   * @param config optional config for the API call e.g. setting specific headers
   */
  protected async edit<T, R = AxiosResponse<T>>(
    url: string,
    id: string,
    data: T,
    config?: CustomAxiosRequestConfig | undefined
  ): Promise<R> {
    return this.axiosInstance.patch(`${url}/${id}`, data, config);
  }

  /**
   * Implements a generic /PATCH call to a given API endpoint via axios to edit (only parts of) one object.
   * This method is used when the full API URL is already known or does not follow the `/url/{id}` pattern.
   *
   * @param url full API path to which the call is sent
   * @param data data which is sent to the API that should be edited
   * @param config optional config for the API call e.g. setting specific headers
   */
  protected async patch<T, R = AxiosResponse<T>>(
    url: string,
    data: T,
    config?: AxiosRequestConfig | undefined
  ): Promise<R> {
    return this.axiosInstance.patch(url, data, config);
  }

  /**
   * Implements the /PUT call to API endpoint (/url/{id}) via axios to update the whole object with the given id of the given type.
   * @param url API path to which the call is sent
   * @param id identifier of the object that should be updated
   * @param data data which is send to the API that should be updated
   * @param config optional config for the API call e.g. setting specific headers
   */
  protected async update<T, R = AxiosResponse<T>>(
    url: string,
    id: string,
    data: T,
    config?: CustomAxiosRequestConfig | undefined
  ): Promise<R> {
    return this.axiosInstance.put(`${url}/${id}`, data, config);
  }

  /**
   * Implements the /DELETE call to API endpoint (/url/{id}) via axios to delete one object with the given id of the given type.
   * @param url API path to which the call is sent
   * @param id identifier of the object that should be deleted
   * @param config optional config for the API call e.g. setting specific headers
   */
  protected async delete<T, R = AxiosResponse<T>>(
    url: string,
    id: string,
    config?: CustomAxiosRequestConfig | undefined
  ): Promise<R> {
    return this.axiosInstance.delete(`${url}/${id}`, config);
  }

  protected canAttempt(urlWithParams: string) {
    if (attemptUrl !== urlWithParams) {
      attemptUrl = urlWithParams;
      attempts = 0;
      return true;
    }

    if (attempts > maxAttempsWithError) return false;

    return true;
  }
}

// TODO: Move all errorMessages to a separate?
